import React from "react"
import { Modal, Form } from "react-bootstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import PrimaryButton from "../subComponents/PrimaryButton"
import SecondaryButton from "../subComponents/SecondaryButton"
import { toast } from "react-toastify"
import { request } from "../../services/request"
import { useParams } from "react-router-dom"
import { formatDollars } from "../../utils/utils"

type Props = {
  show: boolean
  onClose: () => void
  fetchCustomerBills: any
}

/**
 * A single, one-off charge (or a comp), not a subscription.
 *
 * The route is still POST /payment/DirectSubscribe for API compatibility, but the handler
 * behind it is recordDirectPurchase and it creates no Stripe Subscription. Recurring
 * pricing is a seat tier, set up from the billing panel.
 */
function BillModel({ show, onClose, fetchCustomerBills }: Props) {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [confirming, setConfirming] = React.useState(false)
  const param = useParams()

  const validationSchema = Yup.object().shape({
    bill_type: Yup.string()
      .oneOf(["Sensor", "SOS"], "Invalid Product type")
      .required("Please select Product type"),
    amount: Yup.number()
      .typeError("Amount must be a number")
      .required("Please enter amount")
      .min(0, "Amount must be positive"),
    notes: Yup.string(),
    bill_date: Yup.string().required("Please select bill date"),
    product_count: Yup.number()
      .required("Please enter Product Count")
      .min(1, "Product Count must be at least 1"),
  })

  const formik = useFormik({
    initialValues: {
      bill_type: "Sensor",
      // Left blank on purpose. This used to be prefilled from the annual subscription
      // rate, which is the wrong figure for a one-off: the amount here is per unit for
      // this single purchase, not a yearly seat price.
      amount: "",
      notes: "",
      bill_date: "",
      product_count: 1,
    },
    validationSchema,
    onSubmit: async () => {
      // Second stage: the confirmation panel does the actual submitting.
      setConfirming(true)
    },
  })

  const unitAmount = Number(formik.values.amount || 0)
  const count = Number(formik.values.product_count || 0)
  const total = unitAmount * count
  const isComp = unitAmount === 0

  const close = () => {
    onClose()
    setConfirming(false)
    formik.resetForm()
  }

  const submitCharge = async () => {
    // Guarded against a double click: each call creates its own bill row, and the
    // backend keys Stripe idempotency on that row id, so two submits are two charges.
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await request({
        url: `/payment/DirectSubscribe`,
        method: "POST",
        data: {
          userId: param.id,
          type: formik.values.bill_type,
          amount: formik.values.amount,
          date: formik.values.bill_date,
          notes: formik.values.notes,
          product_count: formik.values.product_count,
        },
      })
      // apiClient's response interceptor rejects any non-2xx, so reaching here means the
      // record was created. This endpoint answers 200 whether or not the Stripe charge
      // went through, so claiming the payment itself succeeded would be a guess - say
      // what is actually known, and let the bill list show how it settled.
      toast.success(
        isComp
          ? "Comped purchase recorded."
          : "Purchase recorded. Check the bill status below to confirm the payment went through.",
      )
      close()
      fetchCustomerBills()
    } catch (error: any) {
      toast.error(`${error.message}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      className='comman-modal'
      show={show}
      onHide={() => {
        if (!isSubmitting) close()
      }}
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
      centered
    >
      <Modal.Header closeButton={!isSubmitting} className='comman-modal-header'>
        <Modal.Title className='comman-modal-title'>
          {confirming ? "Confirm this charge" : "One-time charge or comp"}
        </Modal.Title>
      </Modal.Header>

      {confirming ? (
        <Modal.Body className='comman-modal-body'>
          {isComp ? (
            <p>
              Record <strong>{count}</strong> {formik.values.bill_type} at{" "}
              <strong>no charge</strong>. Nothing is sent to Stripe and the customer is not billed
              for this purchase.
            </p>
          ) : (
            <p>
              Charge this customer{" "}
              <strong>
                {formatDollars(unitAmount)} x {count} = {formatDollars(total)}
              </strong>{" "}
              for {formik.values.bill_type}. This is taken from their card immediately.
            </p>
          )}
          <p className='text-xs'>
            {isComp
              ? "This waives this one purchase only. It does not exempt the customer from their recurring subscription, which still bills for any device they provision. A full exemption is on the billing panel."
              : "This is a single charge, separate from their recurring subscription."}
          </p>
          <div className='d-flex gap-3 justify-content-center mt-3'>
            <SecondaryButton
              type='button'
              label='Back'
              className='editFormButton'
              onClick={() => setConfirming(false)}
              disabled={isSubmitting}
            />
            <PrimaryButton
              type='button'
              label={isSubmitting ? "Recording..." : isComp ? "Record comp" : "Charge now"}
              className='editFormButton'
              onClick={submitCharge}
              disabled={isSubmitting}
            />
          </div>
        </Modal.Body>
      ) : (
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body className='comman-modal-body'>
            <div className='login-input-section input-section mb-3'>
              <div>
                <label htmlFor='bill_type'>Product Type</label>
              </div>
              <Form.Select
                id='bill_type'
                name='bill_type'
                value={formik.values.bill_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.bill_type && formik.touched.bill_type}
              >
                <option value='Sensor'>Sensor</option>
                <option value='SOS'>SOS</option>
              </Form.Select>
              <span className='text-red-500 text-xs'>{formik.errors.bill_type}</span>
            </div>

            <div className='login-input-section input-section mb-3'>
              <div>
                <label>
                  Amount per unit{" "}
                  <span className='font-sm'>
                    (set <span className='text-danger'>0</span> to comp it, with no payment taken)
                  </span>
                </label>
              </div>
              <Form.Control
                type='number'
                name='amount'
                placeholder='Please Enter Amount'
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.amount && formik.touched.amount}
              />
              <span className='text-red-500 text-xs'>{formik.errors.amount}</span>
            </div>

            <div className='login-input-section input-section mb-3'>
              <div>
                <label>Product Count</label>
              </div>
              <Form.Control
                type='number'
                name='product_count'
                placeholder='Please Enter Product Count'
                value={formik.values.product_count}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                min={1}
                isInvalid={!!formik.errors.product_count && formik.touched.product_count}
              />
              <span className='text-red-500 text-xs'>{formik.errors.product_count}</span>
            </div>

            <div className='login-input-section input-section mb-3'>
              <div>
                <label>Bill Date</label>
              </div>
              <Form.Control
                type='date'
                name='bill_date'
                value={formik.values.bill_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.bill_date && formik.touched.bill_date}
              />
              <span className='text-red-500 text-xs'>
                {formik.touched.bill_date && formik.errors.bill_date
                  ? formik.errors.bill_date
                  : null}
              </span>
            </div>

            <div className='login-input-section input-section mb-3'>
              <div>
                <label>Notes</label>
              </div>
              <Form.Control
                as='textarea'
                rows={3}
                name='notes'
                placeholder='Please Enter Notes'
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={!!formik.errors.notes && formik.touched.notes}
              />
              <span className='text-red-500 text-xs'>{formik.errors.notes}</span>
            </div>

            <PrimaryButton type='submit' label='Review' className='editFormButton' />
          </Modal.Body>
        </Form>
      )}
    </Modal>
  )
}

export default BillModel
